import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismButtonGroup,
  PrismCard,
  PrismNumberInput,
  PrismSelectInput,
  PrismToggle,
} from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import {
  PA_AUTOPILOT_SCHEDULE_OPTIONS,
  type PaAutopilotSettings,
} from 'frontend-utils/models/hotel';
import { REVERSE_ISO_DAYS } from 'frontend-utils/utils/constants/constant-values';
import SettingsAutopilotCalendarsExceptionDates from 'lumen/components/settings/autopilot/calendars-exception-dates';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type HotelSettingsAutopilotRoute from 'lumen/routes/hotel/settings/autopilot';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  MAX_ALLOWED_MONTHS_IN_FUTURE,
  NUMBER_INPUT_MAX_VALUE,
  PA_AUTOPILOT_LEADTIME_MAX_VALUE,
  PA_AUTOPILOT_SCHEDULE_OPTIONS_ORDER,
  PA_AUTOPILOT_SCHEDULE_OPTIONS_TRANSLATION_KEY_MAPPING,
  PRICE_DIFFERENCE_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import safeMultiply from 'lumen/utils/numeric/safe-multiply';
import {
  formatAutopilotFrequencyMessage,
  formatTimeTo12HourFormat,
  formatTimezoneName,
} from 'lumen/utils/pricing-assistant/autopilot-frequency-formatting';
import type { ModelFrom } from 'lumen/utils/routes';
import { timeout } from 'lumen/utils/waiters';

interface SettingsAutopilotAutopilotSignature {
  Args: {
    hasUserChangedAutopilotSettingsValue: boolean;
    model: ModelFrom<HotelSettingsAutopilotRoute>;
    updateHasUserChangedAutopilotSettingsValue: (hasChanged: boolean) => void;
  };
}

export default class SettingsAutopilot extends Component<SettingsAutopilotAutopilotSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Make a local copy of the current Pricing Assistant Autopilot settings. This local copy will be used to render the state of the page and to store the user's changes before saving.
  @tracked localSettingsCopy = JSON.parse(
    JSON.stringify(this.paAutopilotSettings),
  ) as PaAutopilotSettings;

  debouncedNewLeadTimeValueTask = task(
    { restartable: true },
    async (value: number | null): Promise<void> => {
      await timeout();

      this.localSettingsCopy.min_push_lead_time = value;
      this.updateLocalSettingsCopy();
    },
  );

  get areAutopilotFrequencyOptionsLoading(): boolean {
    return this.args.model.pricingAssistantAutopilotScheduleOptionsTask
      .isRunning;
  }

  get areAutopilotSettingsActionOrInputDisabled(): boolean {
    return (
      this.access.isPricingAssistantSettingsActionOrInputDisabled ||
      !this.localSettingsCopy.enabled
    );
  }

  get areAutopilotSettingsUpdating(): boolean {
    return this.args.model.saveAndUpdateAutopilotSettings.isRunning;
  }

  get areNoAutopilotDaysSelected(): boolean {
    // Number of days in the range [today, lastAllowedMonthEndDate]
    // Note: diff() does not count the last date, so we add 1 to the result
    const totalNumberOfDaysInRange =
      this.lastAllowedMonthEndDate.diff(this.today, 'day') + 1;

    // Check if exclusion_dates array length is same as the number of days in the range i.e. all days are excluded days from autopilot
    return (
      this.localSettingsCopy.exclusion_dates.length === totalNumberOfDaysInRange
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get autopilotFrequencyOptions() {
    if (this.areAutopilotFrequencyOptionsLoading) {
      // Return selected schedule option as the only option while the fetch task is running
      return [this.localSettingsCopy.settings.schedule];
    }

    return (
      this.args.model.pricingAssistantAutopilotScheduleOptionsTask.value ?? []
    );
  }

  get autopilotLeadtimeInlineLabelText(): string {
    return this.intl.t('general.days.label', { count: this.leadtimeValue });
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedAutopilotFrequencyOptions() {
    const scheduleOptions = this.autopilotFrequencyOptions
      .map((option) => {
        if (!option.id) {
          return null;
        }

        const formattedTimes = option.times.map(formatTimeTo12HourFormat);

        let formattedTimeString = '';

        if (option.id !== PA_AUTOPILOT_SCHEDULE_OPTIONS.HOURLY) {
          formattedTimeString = formattedTimes.join(' / ');

          const formattedTimezoneName = formatTimezoneName(
            this.hotelAndMarketService,
          );

          // Append timezone name to the time if it exists
          if (formattedTimezoneName) {
            formattedTimeString = `${formattedTimeString} ${formattedTimezoneName}`;
          }
        }

        const optionId =
          option.id as keyof typeof PA_AUTOPILOT_SCHEDULE_OPTIONS_TRANSLATION_KEY_MAPPING;

        return {
          id: optionId,
          label: this.intl.t(
            PA_AUTOPILOT_SCHEDULE_OPTIONS_TRANSLATION_KEY_MAPPING[optionId],
          ),
          unsafe_subLabel: formattedTimeString,
        };
      })
      .filter((option) => option !== null);

    // sort the options
    scheduleOptions.sort(
      (a, b) =>
        PA_AUTOPILOT_SCHEDULE_OPTIONS_ORDER[a.id] -
        PA_AUTOPILOT_SCHEDULE_OPTIONS_ORDER[b.id],
    );

    return scheduleOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDaySegmentedButtons() {
    const days = [];
    let dow = dayjs().startOf('week');

    while (dow.isSameOrBefore(dayjs().endOf('week'))) {
      const weekdayId = dow.isoWeekday();
      const isoWeekday = REVERSE_ISO_DAYS[weekdayId];

      days.push({
        id: weekdayId.toString(),
        value: this.intl.t(`days_short.${isoWeekday}`),
        longValue: this.intl.t(`days.${isoWeekday}`),
        isSelected: this.localSettingsCopy.rules[0]?.by_day.includes(weekdayId),
      });

      dow = dow.add(1, 'day');
    }

    return days;
  }

  get formattedMaximumOccupancyValue(): number | null {
    return safeMultiply(this.localSettingsCopy.settings.max_occupancy, 100);
  }

  get formattedMinimumOccupancyValue(): number | null {
    return safeMultiply(this.localSettingsCopy.settings.min_occupancy, 100);
  }

  get formattedMinimumPriceDifferenceValue(): number | null {
    let minimumPriceDifferenceValue: number | null =
      this.localSettingsCopy.settings.min_price_diff.value;

    if (
      this.selectedMinimumPriceDifferenceType?.id ===
      PRICE_DIFFERENCE_TYPES.RELATIVE
    ) {
      minimumPriceDifferenceValue = safeMultiply(
        minimumPriceDifferenceValue,
        100,
      );
    }

    return minimumPriceDifferenceValue;
  }

  // One of the autopilot setting is in error state
  get hasInvalidAutopilotSettings(): boolean {
    return (
      this.areNoAutopilotDaysSelected ||
      this.hasInvalidLeadtimeValue ||
      this.localSettingsCopy.settings.min_price_diff.value === null ||
      this.localSettingsCopy.settings.min_occupancy === null ||
      this.localSettingsCopy.settings.max_occupancy === null ||
      this.hasInvalidOccupancyThresholdValues
    );
  }

  get hasInvalidLeadtimeValue(): boolean {
    return this.leadtimeValue === null;
  }

  // The second threshold level needs to be bigger than the first threshold level or the given input isn't correct
  get hasInvalidOccupancyThresholdValues(): boolean {
    // Only do the comparison if both values are not null
    if (
      this.formattedMaximumOccupancyValue !== null &&
      this.formattedMinimumOccupancyValue !== null
    ) {
      return (
        this.formattedMaximumOccupancyValue <=
        this.formattedMinimumOccupancyValue
      );
    }

    return false;
  }

  get isDiscardButtonDisabled(): boolean {
    return (
      this.access.isPricingAssistantSettingsActionOrInputDisabled ||
      !this.args.hasUserChangedAutopilotSettingsValue ||
      this.areAutopilotSettingsUpdating
    );
  }

  get isSaveButtonDisabled(): boolean {
    return (
      this.access.isPricingAssistantSettingsActionOrInputDisabled ||
      !this.args.hasUserChangedAutopilotSettingsValue ||
      this.areAutopilotSettingsUpdating ||
      this.hasInvalidAutopilotSettings
    );
  }

  // Last day of the last month shown
  get lastAllowedMonthEndDate(): Dayjs {
    return this.today.add(MAX_ALLOWED_MONTHS_IN_FUTURE, 'month').endOf('month');
  }

  get leadtimeValue(): number | null {
    return this.localSettingsCopy.min_push_lead_time;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minimumPriceDifferenceTypeOptions() {
    return [
      {
        id: PRICE_DIFFERENCE_TYPES.ABSOLUTE,
        label: this.hotelAndMarketService.hotel.currencyCode,
      },
      {
        id: PRICE_DIFFERENCE_TYPES.RELATIVE,
        label: this.intl.t(
          'pricing_assistant.edit_display_modal.label_set_difference.percentage',
        ),
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get paAutopilotSettings() {
    return this.hotelAndMarketService.hotel.paAutopilotSettings;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedAutopilotFrequency() {
    // Find selected frequency option from formatted autopilot frequency options
    return this.formattedAutopilotFrequencyOptions.find(
      (option) => option.id === this.localSettingsCopy.settings.schedule.id,
    );
  }

  get selectedDaySegmentedButtons(): string[] {
    return this.formattedDaySegmentedButtons
      .filter((button) => button.isSelected)
      .map((button) => button.id);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedMinimumPriceDifferenceType() {
    return this.minimumPriceDifferenceTypeOptions.find(
      (option) =>
        option.id === this.localSettingsCopy.settings.min_price_diff.type,
    );
  }

  get today(): Dayjs {
    return this.hotelAndMarketService.getLocalTime();
  }

  // Find all dates corresponding to isoWeekday in the range [dateStart, dateEnd]
  fetchWeekdayDatesInRange(
    dateStart: dayjs.Dayjs,
    dateEnd: dayjs.Dayjs,
    isoWeekday: number,
  ): string[] {
    // Find first date from start date (today) that corresponds to the passed isoWeekday
    let currentDate = dateStart;
    while (currentDate.isoWeekday() !== isoWeekday) {
      currentDate = currentDate.add(1, 'day');
    }

    // Get all dates corresponding to isoWeekday
    // Start from firstDate and add 1 week at a time until we reach dateEnd
    const dates = [];
    while (currentDate.isSameOrBefore(dateEnd, 'day')) {
      dates.push(currentDate.format('YYYY-MM-DD'));
      currentDate = currentDate.add(1, 'week');
    }

    return dates;
  }

  @action
  onDaySegmentedButtonClick(selectedButtonIds: string[] | string): void {
    const selectedDays = Array.isArray(selectedButtonIds)
      ? selectedButtonIds
      : [selectedButtonIds];

    const previousSelection = this.selectedDaySegmentedButtons;
    const buttonClickedId =
      selectedDays.length > previousSelection.length
        ? selectedDays.find((day) => !previousSelection.includes(day))
        : previousSelection.find((day) => !selectedDays.includes(day));

    // Update by_day rule
    if (this.localSettingsCopy.rules?.[0]?.by_day) {
      this.localSettingsCopy.rules[0].by_day = selectedDays.map(Number);
    }

    // Find all dates corresponding to buttonClicked day in the range [today, lastAllowedMonthEndDate]
    const matchedDates = this.fetchWeekdayDatesInRange(
      this.today,
      this.lastAllowedMonthEndDate,
      Number(buttonClickedId),
    );

    // Update exclusion_dates
    if (buttonClickedId && selectedDays.includes(buttonClickedId)) {
      // If button is selected i.e. autopilot enabled, remove the corresponding dates from exclusion_dates
      this.localSettingsCopy.exclusion_dates =
        this.localSettingsCopy.exclusion_dates.filter(
          (date) => !matchedDates.includes(date),
        );
    } else {
      // If button is unselected i.e. autopilot disabled, add corresponding dates to exclusion_dates
      this.localSettingsCopy.exclusion_dates =
        this.localSettingsCopy.exclusion_dates.concat(matchedDates);
    }

    this.updateLocalSettingsCopy();
  }

  @action
  onLeadtimeValueChange(updatedValue: number | null): void {
    if ((updatedValue ?? 0) <= PA_AUTOPILOT_LEADTIME_MAX_VALUE) {
      void this.debouncedNewLeadTimeValueTask.perform(updatedValue);
    }
  }

  @action
  resetLocalSettingsCopy(): void {
    this.updateLocalSettingsCopy(
      JSON.parse(
        JSON.stringify(this.hotelAndMarketService.hotel.paAutopilotSettings),
      ) as PaAutopilotSettings,
    );

    // Reset our naive change detection when resetting the local copy (+ also triggered when leaving the route or discarding changes)
    this.args.updateHasUserChangedAutopilotSettingsValue(false);
  }

  @action
  async saveCurrentAutopilotSettingsValues(
    refetchFeatureFlags: boolean | MouseEvent = false,
  ): Promise<void> {
    if (this.access.hasPricingAssistantSettingsEditRights) {
      this.usageAnalytics.track('Autopilot Settings Changed', {
        eventTrigger: 'autopilot-settings',
        product: 'pricing-manager',
      });

      let result = null;

      result = await this.args.model.saveAndUpdateAutopilotSettings.perform(
        this.localSettingsCopy,
      );

      if (result.isSuccess) {
        // Update the local settings copy since the save was successful
        this.resetLocalSettingsCopy();

        // Show appropriate toast message depending on whether autopilot is enabled or disabled
        if (this.paAutopilotSettings?.enabled) {
          const formattedMessage = formatAutopilotFrequencyMessage(
            this.localSettingsCopy.settings.schedule,
            this.hotelAndMarketService,
            this.intl,
          );

          this.toast.success(formattedMessage, {
            title: this.intl.t(
              'pricing_assistant.autopilot.toast.title.enable_autopilot',
            ),
            showIcon: true,
            timeout: TOAST_TIMEOUT_LONG,
          });
        } else {
          this.toast.success(
            this.intl.t(
              'pricing_assistant.autopilot.toast.message.disable_autopilot',
            ),
            {
              title: this.intl.t(
                'pricing_assistant.autopilot.toast.title.disable_autopilot',
              ),
              showIcon: true,
              timeout: TOAST_TIMEOUT_LONG,
            },
          );
        }

        if (refetchFeatureFlags) {
          // Refetch pricing_assistant.autopilot.banner FF in case the autopilot is enabled or disabled
          // Refetch caxxxPmsSettings customer_care_access_given boolean (BE disables CC Access when autopilot is enabled)
          await this.access.refetchFeatureFlags();
        }
      }
    }
  }

  @action
  selectedAutopilotFrequencyChanged(
    frequencyOption: (typeof this.formattedAutopilotFrequencyOptions)[number],
  ): void {
    // Find frequency option from autopilot frequency options
    const selectedOption = this.autopilotFrequencyOptions.find(
      (option) => option.id === frequencyOption.id,
    );

    if (!selectedOption) {
      return;
    }

    // selectedOption is a model instance, so we need to extract the id and times from it
    this.localSettingsCopy.settings.schedule = {
      id: selectedOption.id as keyof typeof PA_AUTOPILOT_SCHEDULE_OPTIONS,
      times: selectedOption.times,
    };
    this.updateLocalSettingsCopy();
  }

  @action
  selectedMinimumPriceDifferenceTypeChanged(
    type: (typeof this.minimumPriceDifferenceTypeOptions)[number],
  ): void {
    let minimumPriceDifferenceValue: number | null =
      this.localSettingsCopy.settings.min_price_diff.value;

    if (minimumPriceDifferenceValue !== null) {
      // Update min price difference value accordingly based on the type change
      if (type.id === PRICE_DIFFERENCE_TYPES.RELATIVE) {
        minimumPriceDifferenceValue /= 100;
      } else if (type.id === PRICE_DIFFERENCE_TYPES.ABSOLUTE) {
        minimumPriceDifferenceValue = safeMultiply(
          minimumPriceDifferenceValue,
          100,
        );
      }
    }

    this.localSettingsCopy.settings.min_price_diff.type = type.id;
    this.localSettingsCopy.settings.min_price_diff.value =
      minimumPriceDifferenceValue;
    this.updateLocalSettingsCopy();
  }

  @action
  toggleAutopilotEnabledBoolean(): void {
    this.localSettingsCopy.enabled = !this.localSettingsCopy.enabled;
    this.updateLocalSettingsCopy();

    if (this.localSettingsCopy.enabled) {
      this.usageAnalytics.track('Autopilot Fully Enabled', {
        eventTrigger: 'autopilot-settings',
        product: 'pricing-manager',
      });
    } else {
      this.usageAnalytics.track('Autopilot Fully Disabled', {
        eventTrigger: 'autopilot-settings',
        product: 'pricing-manager',
      });
    }

    // Autopilot enable/disable should be instant, so we save the settings immediately
    void this.saveCurrentAutopilotSettingsValues(true);
  }

  @action
  updateLocalSettingsCopy(
    updatedSettingsObject: PaAutopilotSettings | null = null,
  ): void {
    // PRICING ASSISTANT TODO: investigate further if we can use trackedObject here (we're dealing with nested objects/...)

    // localSettingsCopy is tracked, so we use a reassignment here
    if (updatedSettingsObject) {
      this.localSettingsCopy = updatedSettingsObject;
    } else {
      // eslint-disable-next-line no-self-assign
      this.localSettingsCopy = this.localSettingsCopy;
    }

    if (!this.args.hasUserChangedAutopilotSettingsValue) {
      this.args.updateHasUserChangedAutopilotSettingsValue(true);
    }
  }

  @action
  updateMinimumPriceDifferenceValue(value: number | null): void {
    const minimumPriceDifferenceValue = value;

    if (
      minimumPriceDifferenceValue !== null &&
      minimumPriceDifferenceValue <= NUMBER_INPUT_MAX_VALUE
    ) {
      this.localSettingsCopy.settings.min_price_diff.value =
        this.selectedMinimumPriceDifferenceType?.id ===
        PRICE_DIFFERENCE_TYPES.RELATIVE
          ? minimumPriceDifferenceValue / 100
          : minimumPriceDifferenceValue;

      this.updateLocalSettingsCopy();
    }
  }

  @action
  updateOccupancyValue(
    type: 'max_occupancy' | 'min_occupancy',
    value: number | null,
  ): void {
    this.localSettingsCopy.settings[type] = value !== null ? value / 100 : null;
    this.updateLocalSettingsCopy();
  }

  <template>
    <SettingsPageTemplate
      @description={{t "settings.autopilot.description"}}
      @hasAccountSelector={{true}}
      @title={{t "pricing_assistant.autopilot.settings.main_title"}}
      @type="container"
    >
      <div class="autopilot-settings">
        <PrismCard class="autopilot-card">
          <:content>
            <div class="autopilot-card__content">
              <div class="autopilot-card__section">
                <PrismToggle
                  @dataTestId="autopilot-settings-toggle-autopilot-enablement"
                  @isChecked={{this.localSettingsCopy.enabled}}
                  @isDisabled={{this.access.isPricingAssistantSettingsActionOrInputDisabled}}
                  @label={{t
                    "pricing_assistant.autopilot.settings.title.enable_autopilot"
                  }}
                  @onChange={{this.toggleAutopilotEnabledBoolean}}
                />
              </div>
              <div class="autopilot-divider-line"></div>
              <div
                class="autopilot-card__editable-container
                  {{unless
                    this.localSettingsCopy.enabled
                    'autopilot-card__editable-container--disabled'
                  }}"
              >
                <div class="autopilot-card__section">
                  <p class="text-300--bold">{{t
                      "pricing_assistant.autopilot.settings.title.days"
                    }}</p>
                  <PrismButtonGroup
                    @isDisabled={{this.areAutopilotSettingsActionOrInputDisabled}}
                    @isSmall={{true}}
                    @onChange={{this.onDaySegmentedButtonClick}}
                    @value={{this.selectedDaySegmentedButtons}}
                    as |ButtonGroup|
                  >
                    {{#each
                      this.formattedDaySegmentedButtons
                      as |formattedDaySegmentedButton|
                    }}
                      <ButtonGroup.button
                        @label={{formattedDaySegmentedButton.value}}
                        @value={{formattedDaySegmentedButton.id}}
                        class="w-full"
                      />
                    {{/each}}
                  </PrismButtonGroup>
                  <div class="autopilot-card__section--exclusion-dates">
                    <div class="autopilot-card__setting flex flex-col">
                      <p class="text-300--regular">{{t
                          "pricing_assistant.autopilot.settings.label.disable_days"
                        }}</p>
                      <div class="flex items-center gap-200">
                        <div class="autopilot-card__input">
                          <PrismNumberInput
                            @disabled={{this.areAutopilotSettingsActionOrInputDisabled}}
                            @hasErrors={{this.hasInvalidLeadtimeValue}}
                            @inlineLabel={{this.autopilotLeadtimeInlineLabelText}}
                            @max={{PA_AUTOPILOT_LEADTIME_MAX_VALUE}}
                            @min={{0}}
                            @onValueChange={{this.onLeadtimeValueChange}}
                            @placeholder={{t
                              "pricing_assistant.autopilot.settings.placeholder.disable_days_input"
                            }}
                            @removePadding={{true}}
                            @required={{false}}
                            @value={{this.leadtimeValue}}
                            data-test-id="autopilot-leadtime-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="autopilot-card__setting flex flex-col">
                    <p class="text-300--regular">{{t
                        "pricing_assistant.autopilot.settings.label.calendars"
                      }}</p>

                    <div>
                      <SettingsAutopilotCalendarsExceptionDates
                        @areAutopilotSettingsActionOrInputDisabled={{this.areAutopilotSettingsActionOrInputDisabled}}
                        @isAutopilotEnabled={{this.localSettingsCopy.enabled}}
                        @localSettingsCopy={{this.localSettingsCopy}}
                        @updateLocalSettingsCopy={{this.updateLocalSettingsCopy}}
                      />
                    </div>

                    {{! Show error if no day is selected (neither in DOW buttons nor in calendar) }}
                    {{#if this.areNoAutopilotDaysSelected}}
                      <p
                        class="text-300--regular text-critical-default mt-100"
                      >{{t
                          "pricing_assistant.autopilot.settings.autopilot_days.error"
                        }}</p>
                    {{/if}}
                  </div>
                </div>

                <div class="autopilot-divider-line"></div>

                <div
                  class="autopilot-card__section autopilot-card__section--conditions"
                >
                  <p class="text-300--bold">{{t
                      "pricing_assistant.autopilot.settings.title.conditions"
                    }}</p>

                  <div class="autopilot-card__setting flex flex-col">
                    <p class="text-300--regular">{{t
                        "pricing_assistant.autopilot.settings.label.price_difference"
                      }}</p>
                    <div class="flex items-center gap-200">
                      <div class="autopilot-card__input">
                        <PrismNumberInput
                          @disabled={{this.areAutopilotSettingsActionOrInputDisabled}}
                          @inlineLabel={{if
                            (eq
                              this.selectedMinimumPriceDifferenceType.id
                              PRICE_DIFFERENCE_TYPES.RELATIVE
                            )
                            "%"
                            this.hotelAndMarketService.hotel.currencySymbol
                          }}
                          @isTouched={{true}}
                          @max={{NUMBER_INPUT_MAX_VALUE}}
                          @min={{0}}
                          @onValueChange={{this.updateMinimumPriceDifferenceValue}}
                          @removePadding={{true}}
                          @required={{true}}
                          @value={{this.formattedMinimumPriceDifferenceValue}}
                        />
                      </div>

                      <PrismSelectInput
                        @disabled={{this.areAutopilotSettingsActionOrInputDisabled}}
                        @onChange={{this.selectedMinimumPriceDifferenceTypeChanged}}
                        @options={{this.minimumPriceDifferenceTypeOptions}}
                        @value={{this.selectedMinimumPriceDifferenceType}}
                      />
                    </div>
                  </div>

                  <div class="autopilot-card__setting flex flex-col">
                    <p class="text-300--regular">{{t
                        "pricing_assistant.autopilot.settings.label.otb_threshold"
                      }}</p>

                    <div class="flex flex-col">
                      <div class="flex items-center gap-200">
                        <div class="autopilot-card__input">
                          <PrismNumberInput
                            @disabled={{this.areAutopilotSettingsActionOrInputDisabled}}
                            @hasErrors={{this.hasInvalidOccupancyThresholdValues}}
                            @inlineLabel="%"
                            @isTouched={{true}}
                            @max={{NUMBER_INPUT_MAX_VALUE}}
                            @min={{0}}
                            @onValueChange={{fn
                              this.updateOccupancyValue
                              "min_occupancy"
                            }}
                            @removePadding={{true}}
                            @required={{true}}
                            @value={{this.formattedMinimumOccupancyValue}}
                          />
                        </div>
                        <span class="text-300--regular">{{t
                            "general.and"
                          }}</span>
                        <div class="autopilot-card__input">
                          <PrismNumberInput
                            @disabled={{this.areAutopilotSettingsActionOrInputDisabled}}
                            @hasErrors={{this.hasInvalidOccupancyThresholdValues}}
                            @inlineLabel="%"
                            @isTouched={{true}}
                            @max={{NUMBER_INPUT_MAX_VALUE}}
                            @min={{0}}
                            @onValueChange={{fn
                              this.updateOccupancyValue
                              "max_occupancy"
                            }}
                            @removePadding={{true}}
                            @required={{true}}
                            @value={{this.formattedMaximumOccupancyValue}}
                          />
                        </div>
                      </div>
                      {{#if this.hasInvalidOccupancyThresholdValues}}
                        <p
                          class="text-200--regular text-critical-default mt-100"
                        >{{t
                            "pricing_assistant.autopilot.settings.otb_threshold.error.invalid_thresholds"
                          }}</p>
                      {{/if}}
                    </div>
                  </div>
                </div>

                <div class="autopilot-divider-line"></div>

                <div
                  class="autopilot-card__section autopilot-card__section--frequency"
                >
                  <p class="text-300--bold">{{t
                      "pricing_assistant.autopilot.settings.title.autopilot_frequency"
                    }}</p>

                  <div class="autopilot-card__setting flex flex-col">
                    <p class="text-300--regular">{{t
                        "pricing_assistant.autopilot.settings.label.frequency"
                      }}</p>

                    <div class="flex flex-row">
                      <PrismSelectInput
                        @disabled={{or
                          this.areAutopilotSettingsActionOrInputDisabled
                          this.areAutopilotFrequencyOptionsLoading
                        }}
                        @isSecondary={{true}}
                        @onChange={{this.selectedAutopilotFrequencyChanged}}
                        @options={{this.formattedAutopilotFrequencyOptions}}
                        @value={{this.selectedAutopilotFrequency}}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </:content>

          <:footer>
            <div class="autopilot-card__footer gap-300">
              <PrismButton
                @buttonType="secondary"
                @isDisabled={{this.isDiscardButtonDisabled}}
                @label={{t "pricing_assistant.settings.footer.secondary_btn"}}
                @onClick={{this.resetLocalSettingsCopy}}
              />
              <PrismButton
                @isDisabled={{this.isSaveButtonDisabled}}
                @label={{t "general.save"}}
                @onClick={{this.saveCurrentAutopilotSettingsValues}}
              />
            </div>
          </:footer>
        </PrismCard>
      </div>
    </SettingsPageTemplate>
  </template>
}
